import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, updateUser, updateToken, refresh } from '../../store/authSlice';
import { RootState, AppDispatch } from '../../store';
import { useNotifications } from '../../hooks/useNotifications';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import SubscriptionStatusBanner from '../Subscription/SubscriptionStatusBanner';
import { hasPermission, Permission } from '../../constants/permissions';
import {
  HomeIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  CubeIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ReceiptPercentIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  // Initialize notification polling and desktop notifications
  useNotifications();
  
  // Auto-refresh user permissions periodically
  useAutoRefresh();

  // Listen for auth refresh events from API interceptor
  useEffect(() => {
    const handleAuthRefresh = (event: any) => {
      const { user: updatedUser, token: newToken } = event.detail;
      if (updatedUser && newToken) {
        // Update both user and token in Redux store
        dispatch(updateUser(updatedUser));
        dispatch(updateToken(newToken));
        console.log('✅ User permissions refreshed automatically:', updatedUser.permissions);
        console.log('🔄 Sidebar navigation will update with new permissions');
      }
    };

    const handleUserDataUpdate = (event: any) => {
      const { user: updatedUser } = event.detail;
      if (updatedUser && updatedUser.permissions) {
        // Update user data from API responses
        dispatch(updateUser(updatedUser));
        console.log('✅ User data updated from API response:', updatedUser.permissions);
      }
    };

    window.addEventListener('auth:refresh', handleAuthRefresh as EventListener);
    window.addEventListener('auth:userDataUpdate', handleUserDataUpdate as EventListener);
    return () => {
      window.removeEventListener('auth:refresh', handleAuthRefresh as EventListener);
      window.removeEventListener('auth:userDataUpdate', handleUserDataUpdate as EventListener);
    };
  }, [dispatch]);

  // Refresh user data when window gains focus (user comes back to tab)
  // This ensures permissions are updated if changed while user was away
  useEffect(() => {
    let lastRefreshTime = Date.now();
    const REFRESH_COOLDOWN = 30000; // Only refresh once every 30 seconds max

    const handleFocus = async () => {
      const token = localStorage.getItem('token');
      const now = Date.now();
      
      // Only refresh if enough time has passed since last refresh
      if (token && user && (now - lastRefreshTime) > REFRESH_COOLDOWN) {
        try {
          lastRefreshTime = now;
          const refreshResult = await dispatch(refresh());
          if (refresh.fulfilled.match(refreshResult)) {
            console.log('✅ User data refreshed on window focus');
          }
        } catch (error) {
          console.error('Failed to refresh on focus:', error);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [dispatch, user]);

  // Debug: Log user object to check role and permissions
  React.useEffect(() => {
    console.log('Current user in Layout:', user);
    console.log('User role:', user?.role);
    console.log('User permissions:', user?.permissions);
    console.log('Has inventory:view permission:', hasPermission(user, Permission.INVENTORY_VIEW));
  }, [user]);

  // Build navigation based on permissions - Memoized to update when permissions change
  // Using JSON.stringify to ensure changes are detected even if object reference is same
  const permissionsKey = useMemo(() => 
    JSON.stringify(user?.permissions || {}), 
    [user?.permissions]
  );
  
  const navigation = useMemo(() => {
    console.log('🔄 Rebuilding navigation with permissions:', user?.permissions);
    console.log('📋 Available navigation items check:');
    console.log('  - Inventory view:', hasPermission(user, Permission.INVENTORY_VIEW));
    console.log('  - Users view:', hasPermission(user, Permission.USERS_VIEW));
    
    const nav = [
      ...(hasPermission(user, Permission.DASHBOARD_VIEW) ? [{ name: 'Dashboard', href: '/dashboard', icon: HomeIcon }] : []),
      ...(hasPermission(user, Permission.PURCHASE_ORDERS_VIEW) ? [{ name: 'Purchase Orders', href: '/purchase-orders', icon: DocumentTextIcon }] : []),
      ...(hasPermission(user, Permission.INVOICES_VIEW) ? [{ name: 'Invoices', href: '/invoices', icon: DocumentDuplicateIcon }] : []),
      ...(hasPermission(user, Permission.INVENTORY_VIEW) ? [{ name: 'Inventory', href: '/inventory', icon: CubeIcon }] : []),
      ...(hasPermission(user, Permission.RETAIL_BILLS_VIEW) ? [{ name: 'Retail Bills', href: '/retail-bills', icon: ReceiptPercentIcon }] : []),
      ...(hasPermission(user, Permission.USERS_VIEW) ? [{ name: 'Users', href: '/users', icon: UserGroupIcon }] : []),
      ...(hasPermission(user, Permission.SUBSCRIPTIONS_VIEW) ? [{ name: 'Subscription', href: '/subscription', icon: CreditCardIcon }] : []),
      ...(hasPermission(user, Permission.COMPANIES_SETTINGS) ? [{ name: 'Company Settings', href: '/company/settings', icon: BuildingOfficeIcon }] : []),
    ];
    
    console.log('✅ Final navigation items:', nav.map(item => item.name));
    return nav;
  }, [user, permissionsKey]); // Re-compute when user or permissionsKey changes

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex items-center justify-between px-4 py-5 border-b">
            <span className="text-xl font-bold">
              <span className="text-indigo-600">khata</span>
              <span className="text-gray-900">.biz</span>
            </span>
            <button onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1" key={`mobile-nav-${permissionsKey}`}>
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-4 py-3 rounded-lg ${
                  location.pathname.startsWith(item.href)
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex items-center px-6 py-5 border-b">
            <span className="text-2xl font-bold">
              <span className="text-indigo-600">khata</span>
              <span className="text-gray-900">.biz</span>
            </span>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1" key={`desktop-nav-${permissionsKey}`}>
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname.startsWith(item.href)
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="px-4 py-4 border-t">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-gray-500">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow">
          <button
            type="button"
            className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex flex-1 items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                {navigation.find(item => location.pathname.startsWith(item.href))?.name || 'Dashboard'}
              </h1>
            </div>
            <div className="ml-4 flex items-center">
              <NotificationDropdown />
            </div>
          </div>
        </div>

        {/* Subscription Status Banner */}
        <SubscriptionStatusBanner />

        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;